import React, { useCallback, useEffect, useState } from 'react';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../../../components/ui/Drawer';
import RepairOrderCardList from '../../../components/RepairOrderCardList';
import repairOrdersService from '../../../services/repairOrders.service';
import { RepairOrderCard } from '../../../types/repairOrders';
import RepairOrderDrawer from './RepairOrderDrawer';

export interface EquipmentRepairOrdersDrawerProps {
  /** Chassis/truck unit whose repair orders to list; null keeps the drawer closed. */
  unit: string | null;
  onClose: () => void;
}

/**
 * Right-hand sidebar listing every repair order ever submitted against a
 * chassis/truck unit — opened from the ROs count badge on the Repair Orders
 * page. Same cards as the Pro Details ROs sub-tab (RepairOrderCardList), and
 * clicking a card opens the shared RepairOrderDrawer, same as on pros.
 */
const EquipmentRepairOrdersDrawer: React.FC<EquipmentRepairOrdersDrawerProps> = ({ unit, onClose }) => {
  const [rows, setRows] = useState<RepairOrderCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openRecno, setOpenRecno] = useState<number | null>(null);

  const fetchRows = useCallback(async (signal?: AbortSignal) => {
    if (!unit) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await repairOrdersService.getByEquipment(unit, signal);
      setRows(res.data);
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch repair orders for equipment:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load repair orders');
    } finally {
      setLoading(false);
    }
  }, [unit]);

  useEffect(() => {
    const controller = new AbortController();
    void fetchRows(controller.signal);
    return () => controller.abort();
  }, [fetchRows]);

  return (
    <>
      <Drawer
        isOpen={unit !== null}
        onClose={onClose}
        title={(
          <span className="flex items-center gap-2">
            <WrenchScrewdriverIcon className="w-4 h-4 text-slate-600 dark:text-gray-300" />
            <span>Repair Orders — {unit}</span>
            {rows.length > 0 && (
              <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-gray-300">
                {rows.length}
              </span>
            )}
          </span>
        )}
      >
        <div className="p-3">
          <RepairOrderCardList rows={rows} loading={loading} error={error} onCardClick={setOpenRecno} />
        </div>
      </Drawer>

      <RepairOrderDrawer
        isOpen={openRecno !== null}
        onClose={() => setOpenRecno(null)}
        orderRecno={openRecno}
        onSaved={() => { void fetchRows(); }}
      />
    </>
  );
};

export default EquipmentRepairOrdersDrawer;
